import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import type { ClickEvent as ViewClickEvent } from "@arcgis/core/views/input/types.js";
import pinUrl from "./images/esriGreenPin16x26.png";

type IHandle = { remove(): void };

export interface CompactCoordinateOptions {
    decimals?: number;
    pinImageUrl?: string;
    attachToUi?: boolean;
}

export function installCompactCoordinate(
    view: MapView,
    options: CompactCoordinateOptions = {}
) {
    const decimals = Math.max(0, Math.min(10, options.decimals ?? 6));
    const pin = (options.pinImageUrl ?? pinUrl) as string;

    const layer = new GraphicsLayer({ listMode: "hide" });
    (view.map as Map).add(layer);

    const pill = document.createElement("div");
    pill.className = "esri-widget esri-component";
    pill.style.cssText = [
        "display:inline-flex;align-items:center;gap:6px;",
        "background:#6b6b6b;color:#fff;",
        "padding:2px 6px;border-radius:2px;",
        "font-size:12px;line-height:18px;",
        "user-select:none",
    ].join("");

    const armBtn = document.createElement("button");
    armBtn.title = "Toggle pin mode";
    armBtn.type = "button";
    armBtn.setAttribute("aria-pressed", "false");
    armBtn.style.cssText =
        "border:none;background:transparent;cursor:pointer;display:grid;place-items:center;padding:0;margin-right:4px;color:#fff;opacity:.95;";
    armBtn.innerHTML =
        '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M11 2h2v3a7 7 0 0 1 6 6h3v2h-3a7 7 0 0 1-6 6v3h-2v-3a7 7 0 0 1-6-6H2v-2h3a7 7 0 0 1 6-6V2zm1 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10z"/></svg>';
    pill.appendChild(armBtn);

    const text = document.createElement("span");
    text.textContent = "—";
    pill.appendChild(text);

    if (options.attachToUi !== false) {
        view.ui.add(pill, { position: "bottom-left", index: 1 });
    }

    const fmt = (n: number) =>
        n.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        });

    const toReadout = (mp: Point) => {
        const g =
            mp.spatialReference?.isWGS84 || mp.spatialReference?.isGeographic
                ? mp
                : (webMercatorUtils.webMercatorToGeographic(mp) as Point);
        return `${fmt(g.y as number)} ${fmt(g.x as number)} Degrees`;
    };

    let armed = false;
    let moveHandle: IHandle | null = null;
    let clickHandle: IHandle | null = null;
    let marker: Graphic | null = null;

    function enableLive() {
        if (moveHandle) return;
        moveHandle = view.on("pointer-move", (ev: any) => {
            if (armed) return;
            const mp = view.toMap({ x: ev.x, y: ev.y });
            if (mp) text.textContent = toReadout(mp);
        });
    }
    function disableLive() {
        if (moveHandle) { moveHandle.remove(); moveHandle = null; }
    }

    function enablePlotting() {
        if (clickHandle) return;
        clickHandle = view.on("click", (ev: ViewClickEvent) => {
            if (!armed) return;
            const mp = view.toMap({ x: ev.x, y: ev.y });
            if (!mp) return;

            const sym = new PictureMarkerSymbol({
                url: pin,
                width: 16,
                height: 26,
                yoffset: 12,
            });

            if (!marker) {
                marker = new Graphic({ geometry: mp, symbol: sym });
                layer.add(marker);
            } else {
                marker.geometry = mp;
                marker.symbol = sym;
            }

            text.textContent = toReadout(mp);
        });
    }
    function disablePlotting() {
        if (clickHandle) { clickHandle.remove(); clickHandle = null; }
    }

    function updateToggleVisuals() {
        armBtn.setAttribute("aria-pressed", armed ? "true" : "false");
        armBtn.style.color = armed ? "#6ec6ff" : "#ffffff";
        armBtn.style.opacity = armed ? "1" : ".95";
        (view.container as HTMLElement).style.cursor = armed ? "crosshair" : "";
    }


    function armOn() {
        armed = true;
        disableLive();
        enablePlotting();
        updateToggleVisuals();
        if (marker?.geometry) text.textContent = toReadout(marker.geometry as Point);
    }

    function armOff() {
        armed = false;
        disablePlotting()
        if (marker) { try { layer.remove(marker); } catch { } marker = null; }
        enableLive();
        updateToggleVisuals();
    }

    armBtn.onclick = () => {
        armed ? armOff() : armOn();
    };

    enableLive();
    try {
        const center = (view.extent?.center as Point) ?? (view.center as Point);
        if (center) text.textContent = toReadout(center);
    } catch { }

    return {
        element: pill,
        destroy() {
            try {
                moveHandle?.remove();
                clickHandle?.remove();
            } catch { }
            if (marker) { try { layer.remove(marker); } catch { } marker = null; }
            (view.container as HTMLElement).style.cursor = "";
            (view.map as Map).remove(layer);
            pill.remove();
        },
    };
}

